// The one screen-delete confirmation, shared by the Configure tab's danger
// zone, the screens list and the location page's screens table, so all three
// say the same thing about what a delete takes. There is no soft delete: the
// row and its estimate history go (estimates cascade), and the display token
// goes with the row, so any player on the URL goes dark. Type-the-name, like
// the display URL's Regenerate, since it takes a wall down the same way.

import { DestructiveConfirm, toast } from "@pej/kore/components";
import { useDeleteScreen } from "@/api/screens";
import { toastMutationError } from "@/components/common/toast-mutation-error";

export interface ScreenDeleteTarget {
    id: string;
    name: string;
    locationId: string;
}

export interface ScreenDeleteConfirmProps {
    workspaceId: string;
    /** The screen pending deletion; null keeps the dialog closed. */
    screen: ScreenDeleteTarget | null;
    onClose: () => void;
    onDeleted?: () => void;
}

export function ScreenDeleteConfirm({ workspaceId, screen, onClose, onDeleted }: ScreenDeleteConfirmProps) {
    const deleteScreen = useDeleteScreen(workspaceId, screen?.locationId ?? "", screen?.id ?? "");

    async function handleDelete() {
        try {
            await deleteScreen.mutateAsync();
            toast.success("Screen deleted");
            onClose();
            onDeleted?.();
        } catch (err) {
            toastMutationError("Delete failed", err);
        }
    }

    return (
        <DestructiveConfirm
            open={screen !== null}
            onOpenChange={(open) => {
                if (!open) onClose();
            }}
            title="Delete this screen"
            description={
                <>
                    This cannot be undone. <strong>{screen?.name}</strong>'s settings and waiting-time history are
                    removed, and its display URL stops working at once.
                </>
            }
            expectedConfirmation={screen?.name ?? ""}
            echoLabel="Type the screen's name to confirm"
            primaryLabel="Delete"
            confirming={deleteScreen.isPending}
            onConfirm={handleDelete}
        />
    );
}
